/**
 * AnimationStateMachine — pure animation-state resolver.
 *
 * Maps an entity's current condition to one animation state via a fixed
 * priority cascade. Framework-free so the transition rules are unit-tested;
 * a thin Phaser adapter (later, once spritesheets exist) plays the clip named
 * by `state` and only re-plays when `changed` is true.
 */

export type AnimState =
  | 'idle'
  | 'walk'
  | 'run'
  | 'attackL'
  | 'attackH'
  | 'jump'
  | 'hurt'
  | 'ko'
  | 'throw'
  | 'special'
  | 'block'
  | 'taunt';

export interface AnimInputs {
  ko: boolean;
  hurt: boolean;
  /** True while the player holds the BLOCK button. */
  blocking?: boolean;
  throwing: boolean;
  special: boolean;
  attacking: boolean;
  /** Which attack clip when attacking (defaults to attackL). */
  attackState?: 'attackL' | 'attackH';
  airborne: boolean;
  moving: boolean;
  running: boolean;
  /** True while playing a taunt pose (player input or enemy AI-driven). */
  taunting?: boolean;
}

/** Pure: resolve the single active animation state by priority. */
export function resolveAnimState(i: AnimInputs): AnimState {
  if (i.ko) return 'ko';
  if (i.hurt) return 'hurt';
  if (i.blocking) return 'block';
  if (i.throwing) return 'throw';
  if (i.special) return 'special';
  if (i.attacking) return i.attackState ?? 'attackL';
  if (i.airborne) return 'jump';
  if (i.running) return 'run';
  if (i.moving) return 'walk';
  if (i.taunting) return 'taunt';
  return 'idle';
}

export class AnimationStateMachine {
  private state: AnimState = 'idle';

  get current(): AnimState {
    return this.state;
  }

  /** Feed current inputs; returns the state + whether it changed this tick. */
  update(inputs: AnimInputs): { state: AnimState; changed: boolean } {
    const next = resolveAnimState(inputs);
    const changed = next !== this.state;
    this.state = next;
    return { state: next, changed };
  }

  reset(): void {
    this.state = 'idle';
  }
}
